// src/components/TeamDashboardData.jsx
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import { useDispatch, useSelector } from "react-redux";
import { getTeamAssignedFacilities } from "../redux/slices/dashboardSlice";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";

const TeamDashboardData = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const {
    teamAssignedFacilities,
    isTeamAssignedFacilitiesLoading,
    teamAssignedFacilitiesError,
  } = useSelector((state) => state.dashboard);

  // Date filter hook
  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Local state for table controls
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1); // 1-indexed for API
  const [rowsPerPage, setRowsPerPage] = useState(10); // initial from prop
  const [tableKey, setTableKey] = useState(0); // for fade animation

  // Fetch data whenever filters, page, or rowsPerPage change
  useEffect(() => {
    const dateRange = getDateRange();
    dispatch(
      getTeamAssignedFacilities({
        page: currentPage,
        per_page: rowsPerPage,
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      }),
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
  ]);

  // Trigger fade animation when data finishes loading
  useEffect(() => {
    if (!isTeamAssignedFacilitiesLoading && teamAssignedFacilities?.data) {
      setTableKey((prev) => prev + 1);
    }
  }, [isTeamAssignedFacilitiesLoading, teamAssignedFacilities?.data]);

  const handleClick = (rowData) => {
    navigate(`/facility/${rowData.id}`, {
      state: {
        facility: rowData,
        facilityId: rowData.id,
      },
    });
  };

  // Transform API data for table display
  const transformFacilityData = () => {
    if (!teamAssignedFacilities?.data?.length) return [];

    return teamAssignedFacilities.data.map((facility) => ({
      id: facility.id,
      facility: facility.facility_name || "N/A",
      facility_address: facility.facility_address || "N/A",
      customer_group: facility.customer_group_name || "N/A",
      hoursWorked: facility.total_hours || "0",
      formsSubmitted: facility.total_assessments || "0",
      documents: facility.total_assigned_assessments || "0",
      total_tasks: facility.total_tasks || "0",
      total_assigned_users: facility.total_assigned_users || "0",
      budgetedhours: facility.budgeted_hours
        ? Math.round(facility.budgeted_hours)
        : "N/A",
      total_score: facility.total_score || "0",
      percentage: facility.percentage + "%" || "0%",
      originalData: facility,
    }));
  };

  const employeeColumns = [
    {
      name: "facility",
      label: "Facility",
      options: { filter: false, sort: false },
    },
    {
      name: "customer_group",
      label: "Group Name",
      options: { filter: false, sort: false },
    },
    {
      name: "budgetedhours",
      label: "Budgeted Hours",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <span style={{ color: "#666", fontSize: "14px" }}>{value}</span>
        ),
      },
    },
    {
      name: "hoursWorked",
      label: "Hours Worked",
      options: { filter: false, sort: false },
    },
    {
      name: "formsSubmitted",
      label: "Tasks Submitted",
      options: { filter: false, sort: false },
    },
    // {
    //   name: "documents",
    //   label: "Documents",
    //   options: { filter: false, sort: false },
    // },
    {
      name: "percentage",
      label: "Score",
      options: { filter: false, sort: false },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = transformFacilityData()[dataIndex];
          return (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => handleClick(rowData.originalData)}
                style={{
                  background: "#8B2885",
                  border: "none",
                  cursor: "pointer",
                  padding: "5px 10px",
                  color: "white",
                  borderRadius: "7px",
                }}
                disabled={isTeamAssignedFacilitiesLoading}>
                <Icon
                  icon="ic:baseline-remove-red-eye"
                  width="17"
                  height="20"
                  style={{ marginRight: "5px" }}
                />
                View Facility
              </button>
            </div>
          );
        },
      },
    },
  ];

  // MUIDataTable options – serverSide = true is the key fix
  const options = {
    selectableRows: "none",
    rowsPerPageOptions: [5, 10, 25, 50],
    rowsPerPage: rowsPerPage,
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: true,
    filter: false,
    search: false,
    onSearchChange: (searchText) => setSearchText(searchText),
    pagination: true,
    serverSide: true, // ✅ critical – enables server‑side pagination
    count: teamAssignedFacilities?.total || 0,
    page: currentPage - 1, // MUI uses 0‑based index
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setCurrentPage(tableState.page + 1);
          break;
        case "changeRowsPerPage":
          setRowsPerPage(tableState.rowsPerPage);
          setCurrentPage(1); // reset to first page when rows per page changes
          break;
        default:
          break;
      }
    },
    textLabels: {
      body: {
        noMatch: isTeamAssignedFacilitiesLoading
          ? "Loading..."
          : teamAssignedFacilitiesError
            ? `Error: ${teamAssignedFacilitiesError}`
            : "No facilities found for selected period",
      },
    },
  };

  // Loading state (initial load)
  if (
    isTeamAssignedFacilitiesLoading &&
    !teamAssignedFacilities?.data?.length
  ) {
    return (
      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (teamAssignedFacilitiesError && !teamAssignedFacilities?.data?.length) {
    return (
      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>
        <div className="alert alert-danger" role="alert">
          Error loading facilities: {teamAssignedFacilitiesError}
          <button
            className="btn btn-sm btn-outline-danger ms-3"
            onClick={() => {
              const dateRange = getDateRange();
              dispatch(
                getTeamAssignedFacilities({
                  page: 1,
                  per_page: rowsPerPage,
                  from_date: dateRange.from_date,
                  to_date: dateRange.to_date,
                }),
              );
            }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <>
      <style>
        {`
          @keyframes fadeSlideIn {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .fade-table-transition .MuiPaper-root .MuiTableBody-root tr {
            animation: fadeSlideIn 0.25s ease-out forwards;
          }
        `}
      </style>

      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>

        {/* Date Filter & Header */}
        <div className="mb-4 pb-2 border-bottom">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <DateFilter
              {...dateFilter}
              onFilterChange={updateFilter}
              size="sm"
            />
          </div>

          <div className="mt-10 mb-10 d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-light text-dark p-2">
              <i className="fas fa-calendar-alt me-1"></i>
              {getDateRange().label}
            </span>
            {teamAssignedFacilities?.total > 0 && (
              <span className="badge bg-success p-2">
                <i className="fas fa-building me-1"></i>
                {teamAssignedFacilities.total} Facilities
              </span>
            )}
          </div>
        </div>

        {teamAssignedFacilities?.data?.length === 0 &&
        !isTeamAssignedFacilitiesLoading ? (
          <div className="alert alert-info" role="alert">
            No facilities found for {getDateRange().label}.
          </div>
        ) : (
          <div key={tableKey} className="fade-table-transition no-search">
            <MUIDataTable
              data={transformFacilityData()}
              columns={employeeColumns}
              options={options}
              className="overflow-hidden packageTable"
            />
          </div>
        )}

        {/* Stats summary */}
        {teamAssignedFacilities?.total > 0 && (
          <div className="mt-3 text-muted small">
            Showing {transformFacilityData().length} of{" "}
            {teamAssignedFacilities.total} facilities
          </div>
        )}
      </div>
    </>
  );
};

export default TeamDashboardData;
